<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import { usePermittedMembers } from '@/entities/User';
  import { ColoredButton, SelectInput } from '@/shared/ui';
  import { Input } from '@/shared/ui/input';
  import { Label } from '@/shared/ui/label';
  import { Textarea } from '@/shared/ui/textarea';
  import { usePreviousRevisionsModule } from '../../composables/usePreviousRevisionsModule';
  import {
    APPROVAL_CHANGELOG_MAX_LENGTH,
    isApprovalFormComplete,
    type ApprovalConfigValues,
    type ApprovalFormValues,
  } from '../../lib/approval-submission';
  import { handleRevisionKeydown, resolveRevisionPaste } from '../../lib/prefix-input-guards';
  import FormBuilderApprovalConfigFields from './FormBuilderApprovalConfigFields.vue';
  import FormBuilderStageTitle from './FormBuilderStageTitle.vue';

  interface ManagerOption {
    id: string | number;
    name: string;
    disabled?: boolean;
  }

  const props = defineProps<{
    initialPrefix?: string;
    initialRev?: string;
    initialTitle?: string;
    revLocked?: boolean;
    isSubmitting?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'submit', values: ApprovalFormValues): void;
    (e: 'cancel'): void;
  }>();

  // RQ and Título mirror the form header; only REV is typed here.
  const prefix = computed(() => props.initialPrefix ?? '');
  const title = computed(() => props.initialTitle ?? '');
  const rev = ref(props.initialRev ?? '');
  const approvalManagerId = ref<number | ''>('');
  const changelog = ref('');
  const configValues = ref<ApprovalConfigValues>({
    moduleId: '',
    recordOriginId: '',
    recordTypeId: '',
    recordTypeName: '',
    functionalInspectionType: '',
  });

  // The matrix loads asynchronously in edit mode, so seed the field while the user has not touched it.
  watch(
    () => props.initialRev,
    (nextRev) => {
      if (!rev.value || props.revLocked) rev.value = nextRev ?? '';
    }
  );

  const { lockedModule, isLoadingLockedModule, loadPreviousRevisionsModule } =
    usePreviousRevisionsModule();

  watch(
    () => props.initialPrefix,
    (nextPrefix) => {
      void loadPreviousRevisionsModule(nextPrefix ?? '');
    },
    { immediate: true }
  );

  // Never cleared on null: a module the user picked must survive a failed lookup.
  watch(lockedModule, (module) => {
    if (module) configValues.value = { ...configValues.value, moduleId: module.id };
  });

  const { userOptions, isLoading, fetchUsers } = usePermittedMembers({
    requiredPermissions: [{ action: 'manageApproval', subject: 'Homologation' }],
  });

  onMounted(fetchUsers);

  const managerOptions = computed<ManagerOption[]>(() => {
    if (isLoading.value) return [{ id: '', name: 'Carregando gestores...', disabled: true }];
    if (userOptions.value.length > 0) return userOptions.value;
    return [{ id: '', name: 'Nenhum gestor encontrado', disabled: true }];
  });

  const currentValues = computed<ApprovalFormValues>(() => ({
    prefix: prefix.value,
    rev: rev.value,
    title: title.value,
    approvalManagerId: approvalManagerId.value,
    changelog: changelog.value,
    ...configValues.value,
  }));

  const canSubmit = computed(() => isApprovalFormComplete(currentValues.value));
  const showsHeaderHint = computed(() => !prefix.value || !title.value);

  function handleManagerChange(value: string | number) {
    approvalManagerId.value = value === '' ? '' : Number(value);
  }

  function handleSubmit() {
    emit('submit', {
      prefix: prefix.value.trim(),
      rev: rev.value.trim(),
      title: title.value.trim(),
      approvalManagerId: approvalManagerId.value,
      changelog: changelog.value,
      ...configValues.value,
    });
  }

  defineExpose({
    prefix,
    rev,
    title,
    approvalManagerId,
    changelog,
    configValues,
    canSubmit,
    handleSubmit,
  });
</script>

<template>
  <section
    class="flex flex-1 flex-col gap-6 overflow-y-auto bg-gray-12 px-6 py-6"
    data-testid="approval-stage"
    aria-label="Envio para Aprovação"
  >
    <FormBuilderStageTitle title="Envio para Aprovação" />

    <div class="flex flex-col gap-1.5">
      <div class="flex items-start gap-6" data-testid="approval-record-row">
        <div class="flex w-[126px] flex-col gap-1.5">
          <Label class="text-sm font-medium text-gray-07">Registro</Label>
          <div class="flex items-center gap-1.5">
            <span class="text-sm text-gray-03">RQ</span>
            <Input
              :model-value="prefix"
              data-testid="approval-prefix-input"
              placeholder="Ex.: 10.3"
              class="h-11 w-[100px] rounded-lg border-gray-02 bg-white-01"
              disabled
            />
          </div>
        </div>
        <div class="flex w-[100px] flex-col gap-1.5">
          <Label class="text-sm font-medium text-gray-07">Rev.</Label>
          <Input
            v-model="rev"
            data-testid="approval-rev-input"
            placeholder="Ex.: 01"
            class="h-11 w-[100px] rounded-lg border-gray-02 bg-white-01"
            :disabled="isSubmitting || revLocked"
            @keydown="handleRevisionKeydown"
            @paste="rev = resolveRevisionPaste($event, rev)"
          />
        </div>
      </div>
      <span v-if="showsHeaderHint" class="text-sm text-gray-03" data-testid="approval-header-hint"
        >Preencha o RQ e o Título no cabeçalho do formulário.</span
      >
    </div>

    <div class="flex flex-col gap-1.5">
      <Label class="text-sm font-medium text-gray-07">Título</Label>
      <Input
        :model-value="title"
        data-testid="approval-title-input"
        placeholder="Escreva o título do Registro"
        class="h-11 w-full rounded-lg border-gray-02 bg-white-01"
        disabled
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <Label class="text-sm font-medium text-gray-07">Gestor para aprovação:</Label>
      <SelectInput
        :model-value="approvalManagerId"
        :options="managerOptions"
        label="Escolha um colaborador"
        container-class="w-[600px]"
        button-class="h-11 bg-white-01 border border-gray-02 whitespace-nowrap"
        :disabled="isSubmitting"
        @update:model-value="handleManagerChange"
      />
    </div>

    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-1.5">
        <Label class="text-sm font-medium text-gray-07">Changelog</Label>
        <Textarea
          v-model="changelog"
          placeholder="Escreva o que mudou no documento"
          :maxlength="APPROVAL_CHANGELOG_MAX_LENGTH"
          class="h-40 min-h-0 resize-none rounded-lg border-gray-02 bg-white-01 px-4 py-3 text-sm [field-sizing:fixed]"
          :disabled="isSubmitting"
        />
      </div>
      <span class="text-sm text-gray-03">Número máximo de caracteres: 1.500</span>
    </div>

    <FormBuilderApprovalConfigFields
      v-model="configValues"
      :disabled="isSubmitting"
      :module-locked="lockedModule !== null"
      :locked-module-name="lockedModule?.name"
      :is-loading-locked-module="isLoadingLockedModule"
    />

    <div class="flex justify-end gap-6 pb-8">
      <ColoredButton
        text="Cancelar"
        outlined
        border-color="gray-02"
        color="gray-07"
        class="bg-white-01 justify-center"
        data-testid="approval-cancel-btn"
        :disabled="isSubmitting"
        @click="emit('cancel')"
      />
      <ColoredButton
        text="Enviar para aprovação"
        class="justify-center"
        data-testid="approval-submit-btn"
        :disabled="!canSubmit || isSubmitting"
        :loading="isSubmitting"
        @click="handleSubmit"
      />
    </div>
  </section>
</template>
